import { types, flow, cast } from 'mobx-state-tree';
import i18next from 'i18next';
import { selectFolder, sleep } from 'service/utils';

const { application, nxlApi, coreappIpc } = window;

const Language = types.model('Language', {
    locale: types.identifier, // en_US
    value: types.string, // en
    name: types.string, // English
});

export default types
    .model('SystemInfo', {
        platform: types.optional(types.string, ''),
        version: types.optional(types.string, ''),
        launcherInstallPath: types.optional(types.string, ''),
        gameDefaultInstallPath: types.optional(types.string, ''),
        downloadPath: types.optional(types.string, ''),
        enableDownloadThrottle: types.optional(types.boolean, false),
        downloadThrottleMaxSpeed: types.optional(types.number, 0, [null, undefined]),
        languages: types.optional(types.array(Language), [{ locale: '', value: '', name: '' }]),
        diskSpace: types.optional(types.number, 0),
        autorun: types.maybe(types.boolean),
        locale: application.params.locale.replace('_', '-') as string, // en-US
        defaultLocale: application.params.default_locale.replace('_', '-') as string,
        updating: types.optional(types.boolean, false),
        serverTime: types.optional(types.number, 0),
        diffServerTime: types.optional(types.number, 0), // diff time (client time - server time) as seconds
        onMac: types.optional(types.boolean, application.params.on_mac),
    })
    .views((self) => ({
        get lang() {
            return self.locale.split('-')[0]; // en
        },
    }))
    .actions((self) => ({
        fetch: flow(function* () {
            const d: any = yield coreappIpc.send('getSystemInfo');
            self.platform = d.platform;
            self.version = d.version;
            self.launcherInstallPath = d.installPath; // launcher install path
            self.gameDefaultInstallPath = d.defaultInstallPath; // game install path
            self.downloadPath = d.downloadPath;
            self.enableDownloadThrottle = d.enableDownloadThrottle;
            self.downloadThrottleMaxSpeed = d.downloadThrottleMaxSpeed;
            self.diskSpace = d.diskSpace;
            const languages = Object.entries(d.supportedLocales).map(([locale, name]) => ({
                locale,
                value: locale.split('_')[0],
                name: String(name),
            }));
            self.languages = cast(languages);
        }),
        fetchAutorun: flow(function* () {
            const d: any = yield coreappIpc.send('getShortcutStatus', 'startup');
            self.autorun = d.startup;
        }),
        updateAutorun(val: boolean) {
            if (val) nxlApi.system.createShortcut({ location: 'startup' });
            else nxlApi.system.removeShortcut({ location: 'startup' });
            self.autorun = val;
        },
        changeLanguage(locale: string, broadcast: boolean = false) {
            if (broadcast) {
                nxlApi.localization.setLocale({ locale });
                application.params.locale = locale;
            }
            i18next.changeLanguage(locale.split('_')[0]);
            self.locale = locale.replace('_', '-');

            window.ipcRenderer.invoke('setCookie', { name: 'i18next', value: self.lang, url: window.settings.hosts.accounts }); // it's referenced by membership api, check-playable api
        },
        updateDownloadSpeed(speed: number) {
            self.enableDownloadThrottle = speed !== 0;
            self.downloadThrottleMaxSpeed = speed;
            nxlApi.appsInstaller.updateGlobalSettings({
                options: {
                    enableDownloadThrottle: self.enableDownloadThrottle,
                    downloadThrottleMaxSpeed: self.downloadThrottleMaxSpeed,
                },
            });
        },
        updateDefaultInstallPath: flow(function* (path = '') {
            try {
                if (!path) {
                    path = yield selectFolder(self.gameDefaultInstallPath);
                    nxlApi.appsInstaller.setDefaultPaths({ installPath: path }); // it will call this function with path parameter
                } else {
                    self.gameDefaultInstallPath = path;
                }
                return true;
            } catch (e) {
                return false;
            }
        }),
        resetDefaultInstallPath() {
            const drive = self.launcherInstallPath.split('\\')[0];
            nxlApi.appsInstaller.setDefaultPaths({ installPath: `${drive}\\Nexon\\Library` });
        },
        openInstallPath() {
            window.electron.openFolder(self.gameDefaultInstallPath);
        },
        Updated: flow(function* () {
            // self.updated = false;
            // yield sleep(300);
            self.updating = true;
            yield sleep(500);
            self.updating = false;
        }),
        // this tick event is used for checking notification content's expiration
        // it will update all view values which has these server time reference in every 1 min
        setServerTime(val: number) {
            self.serverTime = val / 1000;
            self.diffServerTime = (Date.now() - val) / 1000;
        },
    }));
